<script setup>
import { storeToRefs } from "pinia";
import { computed, ref, onMounted, onBeforeMount } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import AgentLayout from "./layouts/AgentLayout.vue";
import router from "@/router";
import moment from "moment";
import { useUiStore } from "@/stores/ui";
import { useGridPreferencesStore } from "@/stores/gridPreferences";
import { useIntegrationsStore } from "@/stores/integrations";
import { useDataCollectionStore } from "@/stores/dataCollection";
import {
  DxDataGrid,
  DxColumn,
  // DxFilterRow,
  DxHeaderFilter,
  DxToolbar,
  DxItem,
  DxPaging,
  DxPager,
  DxSorting,
  DxScrolling,
  DxColumnChooser,
  DxLoadPanel,
  DxStateStoring,
  DxSearchPanel,
} from "devextreme-vue/data-grid";
//   import DxDropDownButton from "devextreme-vue/drop-down-button";
//   import { default as Button } from "devextreme-vue/button";
import { exportDataGrid } from "devextreme/excel_exporter";
import { exportDataGrid as exportDataGridToPdf } from "devextreme/pdf_exporter";
import { Workbook } from "exceljs";
import saveAs from "file-saver";
import { jsPDF } from "jspdf";
import addStoreByTokenModal from "@/components/integrations/addStoreByToken.vue";
import createIntegrationModal from "@/components/integrations/createIntegration.vue";
import ConnectShopifyModal from "@/components/integrations/shopify/ConnectShopifyModal.vue";
import ShopifyWorkspace from "@/components/integrations/shopify/ShopifyWorkspace.vue";
import { useCompanyStore } from "@/stores/company";

const { companyId } = storeToRefs(useCompanyStore());
const adminPreferenceId = computed(() =>
  localStorage.getItem("adminPreferenceId"),
);
// import useValidation from '@/utils/helpers/validations';

// const { checkLoggedInUserRole } = useValidation();
const uiStore = useUiStore();
const gridPreferencesStore = useGridPreferencesStore();
const integrationsStore = useIntegrationsStore();
const dataCollectionStore = useDataCollectionStore();
let getModalState = computed(() => uiStore.modalState);
const dataGridRefName = ref(null);
let showClearAll = ref(false);
const allowedOperations = ref(["contains"]);
// const buttonData = ref(["Excel", "CSV", "PDF"]);
let availableIntegrations = ref([
  {
    name: "Shopify",
    description:
      "Connect Shopify, discover its locations, then choose which Ravo Sites receive its products, variants and per-location inventory. Read-only access - no orders, customers or payments.",
    logoType: "shopify",
    action: "connect_shopify",
  },
  {
    name: "Vensafe",
    description:
      "Connect an existing Vensafe source for legacy/custom catalogue synchronization. Site setup and device allocation remain managed in Ravo.",
    logoType: "vensafe",
    // badge: "Enterprise",
    // features: [
    //   "5,000+ app integrations",
    //   "Multi-step workflows",
    //   "Conditional logic",
    //   "Scheduled automations",
    //   "Real-time triggers",
    // ],
    action: "add_store_by_token",
  },
  {
    name: "Data Mapping",
    description:
      "Fetch custom data from external systems and create them on the dashboard, our team will contact you to create the integration.",
    logoType: "data_mapping",
    // badge: "Enterprise",
    // features: [
    //   "Enable monetization in Persona Settings first",
    //   "Payment processing",
    //   "Subscription management",
    //   "Refund handling",
    //   "Revenue analytics",
    //   "Webhook notifications",
    // ],
    action: "create_data_mapping_integration",
  },
  {
    name: "Need a Custom Integration?",
    description:
      "Contact our team to discuss custom integrations for your enterprise CRM, payment processor, or other business tools.",
    logoType: "custom_integration",
    // badge: "Enterprise",
    // features: [
    //   "Enable monetization in Persona Settings first",
    //   "Payment processing",
    //   "Subscription management",
    //   "Refund handling",
    //   "Revenue analytics",
    //   "Webhook notifications",
    // ],
    action: "create_custom_integration",
  },
]);

let integrationsArr = ref([]);
let dataCollectionsOptions = ref([]);

// Shopify workspace drawer — opened when connecting completes or when a
// connected Shopify row is selected. `workspaceId` also drives refresh resume.
const shopifyWorkspaceId = ref(null);
const shopifyResumeRunId = ref(null);

// Per-source visual identity for the catalog cards.
const integrationVisuals = {
  shopify: { icon: "bi bi-bag-check-fill", cta: "Connect" },
  vensafe: { icon: "bi bi-shop-window", cta: "Connect" },
  data_mapping: { icon: "bi bi-diagram-3-fill", cta: "Set up" },
  custom_integration: { icon: "bi bi-puzzle-fill", cta: "Contact us" },
};
const visual = (type) =>
  integrationVisuals[type] || { icon: "bi bi-plug-fill", cta: "Connect" };
const isRequest = (type) => type === "custom_integration";

const connectedCount = computed(() => integrationsArr.value.length);

const action = async (action, data) => {
  await uiStore.setModalAction(action);
  uiStore.setModalState(true);
  await uiStore.setModalData(data);

  if (action == "create_data_mapping_integration") {
    router.push({ name: "DataMappingFilesView" });
  }
};

const route = useRoute();
const toast = useToast();

// S16 R12: the selected integration + resumed run are persisted in the URL query
// (?shopify=<integration_id>&run=<run_id>) so a real browser reload re-opens the same
// workspace. On reload the id comes back from the URL and the workspace re-fetches the
// durable integration/run from the backend (ShopifyWorkspace.loadAll) — nothing depends
// on in-memory Pinia state surviving the reload.
const syncWorkspaceUrl = (integrationId, resumeRunId) => {
  const query = { ...route.query };
  if (integrationId) {
    query.shopify = integrationId;
    if (resumeRunId) query.run = resumeRunId;
    else delete query.run;
  } else {
    delete query.shopify;
    delete query.run;
  }
  router.replace({ query }).catch(() => {});
};

// Open the Shopify integration workspace for a connected row.
const openShopifyWorkspace = (integrationId, resumeRunId = null) => {
  shopifyWorkspaceId.value = integrationId;
  shopifyResumeRunId.value = resumeRunId;
  syncWorkspaceUrl(integrationId, resumeRunId);
};

const closeShopifyWorkspace = () => {
  shopifyWorkspaceId.value = null;
  shopifyResumeRunId.value = null;
  syncWorkspaceUrl(null, null);
  getIntegrations();
};

// Rehydrate the workspace selection from the URL after a real reload. The id is read
// from the query and the workspace re-fetches durable state from the backend.
const rehydrateWorkspaceFromUrl = () => {
  const integrationId = route.query.shopify;
  if (integrationId && !shopifyWorkspaceId.value) {
    shopifyWorkspaceId.value = String(integrationId);
    shopifyResumeRunId.value = route.query.run ? String(route.query.run) : null;
  }
};

// The browser lands here as a REAL navigation back from Shopify's OAuth consent screen
// (connect or reauthorize) — `?shopify=<id>` alone also marks an already-open workspace
// (R12 URL state), so it can't tell "just connected" apart from "reopened on refresh".
// `shopify_connected`/`shopify_error` are unique to that redirect (ShopifyOAuthController)
// — surface a one-time toast for whichever is present, then strip just those markers so
// a later refresh of the same workspace URL doesn't repeat it.
const notifyShopifyOAuthRedirect = () => {
  const { shopify_connected, shopify_error } = route.query;
  if (!shopify_connected && !shopify_error) return;

  if (shopify_connected) {
    toast.success("Shopify connected successfully.");
  } else {
    toast.error(`Shopify connection failed (reason: ${shopify_error}). Please try again.`);
  }

  const query = { ...route.query };
  delete query.shopify_connected;
  delete query.shopify_error;
  router.replace({ query }).catch(() => {});
};

// When a connected grid row is a Shopify integration, open its workspace.
const onRowClick = (e) => {
  const row = e?.data;
  if (row && row.integration_type === "shopify") {
    openShopifyWorkspace(row.id);
  }
};

// Called by the connect modal once the mocked OAuth flow reaches
// connected_mapping_required.
const onShopifyConnected = (integration) => {
  resetStoreData();
  if (integration && integration.id) {
    openShopifyWorkspace(integration.id);
  }
  getIntegrations();
};

const dataGrid = computed(() => {
  return dataGridRefName.value.instance;
});

const contentReady = (e) => {
  let sortedExp = getSortedColumns();
  let filterExp = e.component.getCombinedFilter();
  if (filterExp || sortedExp) {
    showClearAll.value = true;
  } else {
    showClearAll.value = false;
  }
  setColumnChooserPosition();
};

const getSortedColumns = () => {
  let allColumns = Array.from(Array(dataGrid.value.columnCount()).keys()).map(
    (index) => dataGrid.value.columnOption(index),
  );
  allColumns = allColumns.find((col) => col.sortIndex != null);
  return allColumns;
};

const setColumnChooserPosition = () => {
  var columnChooserView = dataGrid.value.getView("columnChooserView");
  if (!columnChooserView._popupContainer) {
    columnChooserView._initializePopupContainer();
    columnChooserView.render();
  }
  columnChooserView._popupContainer.option("position", {
    of: dataGrid.value.element(),
    my: "left top",
    at: "left top",
    offset: "0 50",
  });
};

const onEditorPreparing = (e) => {
  if (e.parentType == "filterRow") {
    e.editorOptions.placeholder = "Search";
  }
};

const clearFilter = () => {
  dataGrid.value.clearFilter("row");
  dataGrid.value.clearFilter("header");
  dataGrid.value.clearFilter("search");
  dataGrid.value.clearSorting();
};

const customLoad = async () => {
  await gridPreferencesStore
    .onTableHeader({
      url: "/integrations-grid",
      companyId: companyId.value,
    })
    .catch(() => {});
  let value = gridPreferencesStore.tableHeaders;
  return value;
};

const customSave = (gridState) => {
  gridState.pageIndex = 0;
  gridState.pageSize = 10;
  let value = JSON.stringify(gridState);
  value = value.replace(',"selectedRowKeys":[]', "");

  if (!gridPreferencesStore.tableHeaders) {
    gridPreferencesStore
      .createTableHeaders({
        url: "/integrations-grid",
        headers: value,
        filters: "[]",
        companyId: companyId.value,
      })
      .catch(() => {});
  } else if (value != JSON.stringify(gridPreferencesStore.tableHeaders)) {
    gridPreferencesStore
      .updateTableHeaders({
        url: "/integrations-grid",
        headers: value,
        filters: "[]",
        companyId: companyId.value,
        adminPreferenceId: adminPreferenceId.value,
      })
      .catch(() => {});
  }};

const resetStoreData = () => {
  uiStore.setModalState(false);
  uiStore.setModalData({});
  uiStore.setModalAction(null);
};

const openStoresModalAfterValidateToken = async (data) => {
  await resetStoreData();
  action("check_stores_after_submit__vansafe_token", {
    encryptedToken: data.encryptedToken,
    data: data.data,
  });
};

const refreshGrid = async () => {
  resetStoreData();
  getIntegrations();
};

const onItemClick = (type) => {
  if (type == "Excel") {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Main sheet");
    exportDataGrid({
      component: dataGrid.value,
      worksheet: worksheet,
      customizeCell: function (options) {
        const excelCell = options;
        excelCell.font = { name: "Arial", size: 12 };
        excelCell.alignment = { horizontal: "left" };
      },
    }).then(function () {
      workbook.xlsx.writeBuffer().then(function (buffer) {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          "integrations.xlsx",
        );
      });
    });
    // e.cancel = true;
  } else if (type == "CSV") {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Main sheet");
    exportDataGrid({
      component: dataGrid.value,
      worksheet: worksheet,
      customizeCell: function (options) {
        const excelCell = options;
        excelCell.font = { name: "Arial", size: 12 };
        excelCell.alignment = { horizontal: "left" };
      },
    }).then(function () {
      workbook.csv.writeBuffer().then(function (buffer) {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          "integrations.csv",
        );
      });
    });
  } else if (type == "PDF") {
    const doc = new jsPDF("landscape");
    exportDataGridToPdf({
      jsPDFDocument: doc,
      component: dataGrid.value,
      customizeCell: function (options) {
        const { gridCell, pdfCell } = options;
        if (gridCell.rowType === "data") {
          // data columns style
          pdfCell.styles = {
            font: "times",
            fontStyle: "lato",
            fontSize: 8,
            cellWidth: "auto",
          };
        } else {
          // header columns style
          pdfCell.styles = {
            font: "times",
            fontStyle: "lato",
            fontSize: 8,
            fillColor: "#2680EB",
            textColor: "#FFFFFF",
            cellWidth: "auto",
          };
        }
      },
    }).then(() => {
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        const pageSize = doc.internal.pageSize;
        const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
        const pageHeight = pageSize.height
          ? pageSize.height
          : pageSize.getHeight();
        const header = "Users";
        const headerWidth = doc.getTextDimensions(header).w;
        const footer = `Page ${i} of ${pageCount}`;

        // Header
        doc.text(header, (pageWidth - headerWidth) / 2, 10);

        // Footer
        doc.text(
          footer,
          pageWidth / 2 - doc.getTextWidth(footer) / 2,
          pageHeight - 5,
          { baseline: "bottom" },
        );
      }

      doc.save("integrations.pdf");
    });
  }
};

const getIntegrations = () => {
  integrationsStore
    .fetchIntegrations(companyId.value)
    .then((res) => {
      res.data.map((row) => {
        row.created_at = moment(row.created_at).format("DD/MM/YYYY - hh:mm A");
        row.status_label = row.integration_status
          ? row.integration_status.charAt(0).toUpperCase() +
            row.integration_status.slice(1)
          : "-";
        row.integration_type_label =
          row.integration_type == "vensafe"
            ? "Vensafe"
            : row.integration_type == "shopify"
            ? "Shopify"
            : "On Demand";
      });

      integrationsArr.value = res.data;
      dataGrid.value.endCustomLoading();
    })
    .catch(() => {});
};

const fetchDataCollections = () => {
  dataCollectionStore
    .fetchDataCollectionsSimple(companyId.value)
    .then((res) => {
      dataCollectionsOptions.value = res.data;
    })
    .catch(() => {
    });
};

onMounted(() => {
  // dataGrid.value.beginCustomLoading();
});

onBeforeMount(() => {
  getIntegrations();
  fetchDataCollections();
  // Real reload: re-open the workspace named in the URL and let it re-fetch from backend.
  rehydrateWorkspaceFromUrl();
  notifyShopifyOAuthRedirect();
});
</script>

<template>
  <AgentLayout>
    <!-- <div class="flex items-center">
        <h4 class="grid-header-title">Data Collection</h4>
        <button :title="checkLoggedInUserRole() ? 'You are not authorized to perform this action!': ''" :disabled="checkLoggedInUserRole()" @click="dataCollectionModal('show')" pill class="grid-header-action-btn">
          Add
        </button>
      </div> -->

    <addStoreByTokenModal
      v-if="getModalState"
      @openStoresModalAfterValidateToken="openStoresModalAfterValidateToken"
    />
    <createIntegrationModal
      v-if="getModalState"
      @refreshGrid="refreshGrid"
      :dataCollectionsOptions="dataCollectionsOptions"
    />
    <ConnectShopifyModal
      v-if="getModalState"
      @connected="onShopifyConnected"
      @close="resetStoreData"
    />

    <div class="intg">
      <!-- ── Header ───────────────────────────────────────────── -->
      <header class="intg__head">
        <div class="intg__head-text">
          <span class="intg__eyebrow">Connections</span>
          <h1 class="intg__title">Integrations</h1>
          <p class="intg__sub">
            Connect a source, discover its locations, then choose which Ravo Sites
            receive its catalogue and inventory.
          </p>
        </div>
        <div class="intg__stat">
          <b>{{ connectedCount }}</b>
          <span>Connected</span>
        </div>
      </header>

      <!-- ── Available integrations catalog ───────────────────── -->
      <section class="intg__section">
        <div class="intg__section-head">
          <h2>Available integrations</h2>
          <span class="intg__hint">Choose a source to connect</span>
        </div>

        <div
          v-if="availableIntegrations && availableIntegrations.length"
          class="intg__catalog"
        >
          <article
            v-for="(integration, index) in availableIntegrations"
            :key="index"
            class="intg-card"
            :class="[
              `intg-card--${integration.logoType}`,
              { 'intg-card--request': isRequest(integration.logoType) },
            ]"
            tabindex="0"
            role="button"
            @click="action(integration.action, integration)"
            @keydown.enter="action(integration.action, integration)"
          >
            <div class="intg-card__top">
              <span class="intg-card__logo">
                <i :class="visual(integration.logoType).icon"></i>
              </span>
              <span class="intg-card__go" aria-hidden="true">
                <i class="bi bi-arrow-right"></i>
              </span>
            </div>
            <div class="intg-card__body">
              <div class="intg-card__title-row">
                <h3 class="intg-card__name">{{ integration.name }}</h3>
                <span v-if="integration.badge" class="intg-card__badge">
                  <i class="bi bi-award-fill"></i> {{ integration.badge }}
                </span>
              </div>
              <p class="intg-card__desc">{{ integration.description }}</p>
            </div>
            <div class="intg-card__foot">
              <span class="intg-card__cta">
                {{ visual(integration.logoType).cta }}
                <i class="bi bi-arrow-right-short"></i>
              </span>
            </div>
          </article>
        </div>

        <div v-else class="intg__empty">
          <i class="bi bi-plug"></i>
          <p>No integrations available right now.</p>
        </div>
      </section>

      <!-- ── Connected integrations ───────────────────────────── -->
      <section class="intg__section">
        <div class="intg__section-head">
          <h2>Connected integrations</h2>
          <span class="intg__count-pill">{{ connectedCount }}</span>
        </div>

        <section class="grid-white-section main-page-container-animation card intg__grid">
      <DxDataGrid height="100%"
        :data-source="integrationsArr"
        ref="dataGridRefName"
        :remote-operations="true"
        :showBorders="false"
        :columnAutoWidth="true"
        :allowColumnReordering="true"
        :allowColumnResizing="true"
        :rowAlternationEnabled="false"
        :showColumnLines="false"
        :showRowLines="true"
        :repaint-changes-only="true"
        :two-way-binding-enabled="true"
        :no-data-text="'No integrations yet'"
        @editor-preparing="onEditorPreparing($event)"
        @content-ready="contentReady($event)"
        @row-click="onRowClick($event)"
      >
        <DxLoadPanel :show-indicator="true" :show-pane="true" :enabled="true" />
        <DxScrolling mode="standard" :use-native="true" />
        <DxPaging :page-size="10" />
        <DxStateStoring
          :enabled="true"
          type="custom"
          :customSave="customSave"
          :customLoad="customLoad"
        />

        <DxPager
          :visible="true"
          :allowed-page-sizes="[10, 25, 50, 100]"
          :display-mode="'full'"
          :show-page-size-selector="true"
          :show-info="true"
          :show-navigation-buttons="true"
        />

        <DxSearchPanel :width="200" :visible="true" placeholder="Search ..." />
        <DxSorting mode="single" />
        <DxHeaderFilter :visible="true" />
        <DxColumnChooser :enabled="true" mode="select" />

        <DxToolbar>
          <DxItem location="before" template="columnButtonTemplate" />
          <DxItem location="before" template="exportButtonTemplate" />
          <DxItem location="before" template="clearAllTemplate" />
          <DxItem location="after" name="searchPanel" />
        </DxToolbar>

        <DxColumn
          data-field="name"
          caption="Name"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          data-type="string"
          :allow-searching="true"
        />

        <DxColumn
          data-field="integration_type_label"
          caption="Integration Type"
          :allow-editing="false"
          :allow-header-filtering="true"
          :allow-filtering="true"
          :allow-searching="true"
          data-type="string"
        />

        <DxColumn
          data-field="status_label"
          caption="Status"
          :allow-header-filtering="true"
          :filter-operations="allowedOperations"
          data-type="string"
          :allow-searching="true"
          cell-template="statusCellTemplate"
        />

        <DxColumn
          data-field="created_at"
          format="yyyy/MM/dd HH:mm:ss"
          caption="Created At"
          :allow-editing="false"
          :allow-header-filtering="false"
          :allow-filtering="false"
          :allow-searching="false"
          data-type="string"
        />

        <template #statusCellTemplate="{ data }">
          <span
            class="status-badge"
            :class="`status-${data.data.integration_status}`"
            >{{ data.data.status_label }}</span
          >
        </template>

        <template #exportButtonTemplate>
          <button
            @click="onItemClick('Excel')"
            type="button"
            class="btn action-btn m-5 font-20"
          >
            <i class="bi bi-filetype-xlsx"></i>
          </button>
        </template>

        <template #columnButtonTemplate>
          <button
            @click="dataGrid.showColumnChooser()"
            type="button"
            class="btn action-btn font-20 m-5"
          >
            <i class="bi bi-columns"></i>
          </button>
        </template>

        <template #clearAllTemplate>
          <div v-if="showClearAll" class="clear-cols">
            <div class="custom-line"></div>
            <button
              @click="clearFilter()"
              type="button"
              class="btn action-btn clear-colums-filter m-5"
            >
              Clear Filters
            </button>
          </div>
        </template>
      </DxDataGrid>
        </section>
      </section>
    </div>

    <!-- ── Shopify integration workspace (drawer) ───────────────── -->
    <div
      v-if="shopifyWorkspaceId"
      class="shopify-drawer"
      data-test="shopify-drawer"
    >
      <div class="shopify-drawer__scrim" @click="closeShopifyWorkspace"></div>
      <aside class="shopify-drawer__panel">
        <button
          type="button"
          class="shopify-drawer__close"
          @click="closeShopifyWorkspace"
          aria-label="Close"
          data-test="close-workspace"
        >
          <i class="bi bi-x-lg"></i>
        </button>
        <ShopifyWorkspace
          :key="shopifyWorkspaceId"
          :integration-id="shopifyWorkspaceId"
          :resume-run-id="shopifyResumeRunId"
          @changed="getIntegrations"
          @disconnected="closeShopifyWorkspace"
        />
      </aside>
    </div>
  </AgentLayout>
</template>

<style lang="scss" scoped>
/* ===========================================================
   Integrations — a refined "integration marketplace". Built on
   the app's --primary / --neutral token system so it sits
   natively beside the DevExtreme grid and themes light/dark.
   =========================================================== */
.intg {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-7, 32px);
}

/* ── Header ───────────────────────────────────────────────── */
.intg__head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--spacing-4);
  flex-wrap: wrap;
}
.intg__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--primary-500);
}
.intg__title {
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--neutral-900);
  margin: 6px 0 0;
}
.intg__sub {
  font-size: var(--text-sm);
  color: var(--neutral-500);
  margin: 6px 0 0;
  max-width: 56ch;
  line-height: 1.5;
}
.intg__stat {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 10px 18px;
  border: 1px solid var(--neutral-200);
  border-radius: var(--border-radius-md, 10px);
  background: var(--neutral-0);
  line-height: 1;
}
.intg__stat b {
  font-size: 26px;
  font-weight: 800;
  color: var(--neutral-900);
  font-variant-numeric: tabular-nums;
}
.intg__stat span {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
  margin-top: 5px;
}

/* ── Section scaffolding ──────────────────────────────────── */
.intg__section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
}
.intg__section-head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.intg__section-head h2 {
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--neutral-700);
  margin: 0;
}
.intg__hint {
  font-size: var(--text-xs);
  color: var(--neutral-400);
  margin-left: auto;
}
.intg__count-pill {
  min-width: 22px;
  height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  background: var(--primary-50);
  color: var(--primary-700);
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* ── Catalog ──────────────────────────────────────────────── */
.intg__catalog {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-4);

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.intg-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  padding: var(--spacing-5, 20px);
  background: var(--neutral-0);
  border: 1px solid var(--neutral-200);
  border-radius: 14px;
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}
/* accent wash that fades in on hover, tinted per source */
.intg-card::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(120% 90% at 100% 0%, var(--intg-accent, var(--primary-500)) 0%, transparent 55%);
  opacity: 0;
  transition: opacity 0.25s ease;
  pointer-events: none;
}
.intg-card:hover,
.intg-card:focus-visible {
  transform: translateY(-3px);
  border-color: var(--intg-accent, var(--primary-300));
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.1);
  outline: none;
}
.intg-card:hover::before,
.intg-card:focus-visible::before { opacity: 0.05; }

.intg-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.intg-card__logo {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  font-size: 22px;
  color: #fff;
  background: linear-gradient(140deg, var(--intg-accent, var(--primary-500)), var(--intg-accent-2, var(--primary-700)));
  box-shadow: 0 6px 16px -6px var(--intg-accent, var(--primary-500));
  flex: none;
}
.intg-card__go {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: var(--intg-accent, var(--primary-600));
  background: var(--neutral-100);
  font-size: 14px;
  opacity: 0;
  transform: translateX(-6px);
  transition: opacity 0.2s ease, transform 0.2s ease, background 0.2s ease;
}
.intg-card:hover .intg-card__go,
.intg-card:focus-visible .intg-card__go { opacity: 1; transform: translateX(0); }

.intg-card__body { position: relative; flex: 1; display: flex; flex-direction: column; gap: 7px; }
.intg-card__title-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.intg-card__name {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.25;
  color: var(--neutral-900);
  margin: 0;
}
.intg-card__badge {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--primary-700);
  background: var(--primary-50);
  padding: 3px 8px;
  border-radius: 100px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.intg-card__desc {
  font-size: var(--text-sm);
  color: var(--neutral-500);
  line-height: 1.55;
  margin: 0;
}
.intg-card__foot { position: relative; }
.intg-card__cta {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--intg-accent, var(--primary-600));
}
.intg-card__cta i { font-size: 18px; transition: transform 0.2s ease; }
.intg-card:hover .intg-card__cta i { transform: translateX(3px); }

/* per-source accent variables */
.intg-card--shopify { --intg-accent: #95bf47; --intg-accent-2: #5e8e3e; }
.intg-card--vensafe { --intg-accent: #0d9488; --intg-accent-2: #0f766e; }
.intg-card--data_mapping { --intg-accent: #4f46e5; --intg-accent-2: #4338ca; }
.intg-card--custom_integration { --intg-accent: #d97706; --intg-accent-2: #b45309; }

/* "request" variant — a dashed, quieter call-to-action card */
.intg-card--request {
  background: var(--neutral-50);
  border-style: dashed;
  border-color: var(--neutral-300);
}
.intg-card--request .intg-card__logo {
  background: transparent;
  color: var(--intg-accent);
  border: 2px dashed var(--intg-accent);
  box-shadow: none;
}

/* ── Empty state ──────────────────────────────────────────── */
.intg__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 0;
  border: 1px dashed var(--neutral-200);
  border-radius: 14px;
  color: var(--neutral-400);
}
.intg__empty i { font-size: 28px; }
.intg__empty p { margin: 0; font-size: var(--text-sm); }

/* ── Grid card ────────────────────────────────────────────── */
.intg__grid { margin: 0; }

/* ── Shopify workspace drawer ─────────────────────────────── */
.shopify-drawer { position: fixed; inset: 0; z-index: 1200; }
.shopify-drawer__scrim {
  position: absolute; inset: 0; background: rgba(15, 23, 42, 0.4);
}
.shopify-drawer__panel {
  position: absolute; top: 0; right: 0; height: 100%;
  width: min(720px, 100%); background: var(--neutral-0);
  box-shadow: -8px 0 32px rgba(15, 23, 42, 0.18);
  padding: 28px; overflow-y: auto;
}
.shopify-drawer__close {
  position: absolute; top: 16px; right: 16px; background: none; border: none;
  font-size: 18px; cursor: pointer; color: var(--neutral-500);
}

/* ── Dark mode ────────────────────────────────────────────── */
[data-theme="dark"] {
  .intg__title { color: #e8edf5; }
  .intg__stat { background: #1e2a3e; border-color: #2d3f5a; }
  .intg__stat b { color: #e8edf5; }
  .intg__count-pill { background: #1a2d52; color: #a8c4ff; }

  .intg-card {
    background: #1e2a3e;
    border-color: #2d3f5a;
  }
  .intg-card:hover,
  .intg-card:focus-visible {
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.4);
  }
  .intg-card__name { color: #e8edf5; }
  .intg-card__desc { color: #9aadc4; }
  .intg-card__go { background: #28364e; }
  .intg-card__badge { background: #1a2d52; color: #a8c4ff; }
  .intg-card--request { background: #1a2436; border-color: #34466a; }
}
</style>
